'use client'

import { useRef, useState } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * Above-the-fold entry point for organic tool landing pages, rendered when
 * the `tool-entry-v2` flag is on. Compact H1 + one-line value prop + a
 * framed tool panel where the input and run button are the unmissable first
 * interactive elements. The example chip is a real control: it fills and
 * focuses the input, converting a would-be dead click into a start.
 *
 * The submit contract matches the v1 forms exactly: validate locally, then
 * hand the trimmed URL to the page's existing submit handler (email gate,
 * $5 wall, and API calls all live there, untouched).
 */
interface ToolEntryHeroProps {
  title: string
  valueProp: string
  /** Honest cost/scope line rendered inside the panel, e.g. "Free · tests 14 bots · instant results". */
  trustLine: string
  /**
   * What the visitor must do to see their result, stated BEFORE they click.
   *
   * The 2026-09-19 audit found the single largest loss in the funnel here:
   * 158 of 213 people left before requesting a code. Once someone requests
   * one, 88-94% finish every remaining step — so the problem was never the
   * OTP flow, it was that the email requirement first appeared in a modal
   * AFTER the click, on pages whose own trust line promised "instant
   * results". Omit this only for a tool with no gate (e.g. the paid auditor).
   */
  accessNote?: string
  /** Accessible name for the URL input (aria-label) — placeholder text alone is not a label. */
  inputLabel: string
  placeholder: string
  buttonLabel: string
  /** Value the example chip fills into the input, e.g. "pixelmojo.io". */
  exampleValue: string
  isLoading?: boolean
  onSubmit: (url: string) => void
}

export function ToolEntryHero({
  title,
  valueProp,
  trustLine,
  accessNote,
  inputLabel,
  placeholder,
  buttonLabel,
  exampleValue,
  isLoading = false,
  onSubmit,
}: ToolEntryHeroProps) {
  const [url, setUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  function validate(value: string): boolean {
    if (!value.trim()) {
      setError('Please enter a URL.')
      return false
    }
    let test = value.trim()
    if (!test.startsWith('http://') && !test.startsWith('https://')) {
      test = `https://${test}`
    }
    try {
      const parsed = new URL(test)
      if (!parsed.hostname.includes('.')) {
        setError('Please enter a valid domain (e.g. example.com).')
        return false
      }
    } catch {
      setError('Please enter a valid URL.')
      return false
    }
    setError(null)
    return true
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (validate(url)) {
      onSubmit(url.trim())
    }
  }

  function fillExample() {
    setUrl(exampleValue)
    if (error) setError(null)
    inputRef.current?.focus()
  }

  return (
    <div className='text-center'>
      <h1 className='text-fluid-5xl mb-3 font-heading max-w-3xl mx-auto'>
        {title}
      </h1>
      <p className='lead max-w-2xl mx-auto mb-8'>{valueProp}</p>

      <form
        onSubmit={handleSubmit}
        className='w-full max-w-2xl mx-auto bg-muted/50 rounded-2xl border border-line-strong p-4 sm:p-5 text-left'
      >
        <div className='flex flex-col sm:flex-row gap-3'>
          <div className='relative flex-1'>
            <Search
              size={20}
              className='absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground'
            />
            <input
              ref={inputRef}
              type='text'
              aria-label={inputLabel}
              value={url}
              onChange={e => {
                setUrl(e.target.value)
                if (error) setError(null)
              }}
              placeholder={placeholder}
              className='w-full h-14 pl-12 pr-4 border border-border rounded-full bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-colors text-base'
              disabled={isLoading}
            />
          </div>
          <Button
            type='submit'
            variant='default'
            size='lg'
            loading={isLoading}
            disabled={isLoading}
            className='h-14 px-8'
          >
            {buttonLabel}
          </Button>
        </div>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3'>
          <p className='text-sm text-muted-foreground'>{trustLine}</p>
          <button
            type='button'
            onClick={fillExample}
            disabled={isLoading}
            className='self-start sm:self-auto text-sm text-primary underline underline-offset-4 transition-colors hover:text-primary/80 disabled:opacity-50'
          >
            Try {exampleValue}
          </button>
        </div>
        {accessNote && (
          <p className='text-sm text-muted-foreground mt-2'>{accessNote}</p>
        )}
        {error && <p className='text-sm text-[#F90B8A] mt-2'>{error}</p>}
      </form>
    </div>
  )
}
